import { useTranslation } from 'react-i18next';
import {
  useDeleteExerciseLoadFormula,
  useGetExerciseLoadFormulas,
} from '../../../services/redux/api/appConfigApi';
import { useMemo, useState } from 'react';
import { useAccess } from '../../../hooks/useAccess';
import getTableColumns, { getDefaultSettings } from './helpers/tableColumns';
import { getCellReducers } from './helpers/getCellReducers';
import useFlexibleColumnsTable from '../../../hooks/useFlexibleColumnsTable';
import { LOCAL_STORAGE_KEYS } from '../../../shared/settingsNames';
import { IExerciseLoadFormula } from '../../../types/interfaces/exercise-load-formula';
import { FormikProps, useFormik } from 'formik';

export function useExerciseLoadFormulasComponent(classes) {
  const { t } = useTranslation();
  const { data: exerciseLoadFormulas } = useGetExerciseLoadFormulas();
  const [itemForDelete, setItemForDelete] =
    useState<IExerciseLoadFormula>(null);
  const [itemForEditing, setItemForEditing] =
    useState<IExerciseLoadFormula>(null);
  const [showEditModal, setShowEditModal] = useState<boolean>(false);
  const [filter, setFilter] = useState<string>('');
  const { access } = useAccess();

  const [deleteExerciseLoadFormula] = useDeleteExerciseLoadFormula();
  const { order: defaultOrder, orderBy } = getDefaultSettings(t);
  const allOption = { id: 'all', name: t('common.all') };
  const filterFormik: FormikProps<any> = useFormik({
    enableReinitialize: true,
    initialValues: {
      active: allOption,
    },
    onSubmit: () => {},
  });

  const filteredValues = useMemo(
    () =>
      exerciseLoadFormulas?.filter((formula: IExerciseLoadFormula) => {
        const activeFilterId = filterFormik.values.active?.id ?? 'all';
        const filterValue = filter.toLowerCase();
        const includesFilter =
          String(formula.nameKey ?? '')
            .toLowerCase()
            .includes(filterValue) ||
          String(formula.value ?? '')
            .toLowerCase()
            .includes(filterValue);

        if (!includesFilter) return false;

        if (activeFilterId !== 'all') {
          const isActive = !!formula.active;
          if (
            (activeFilterId === true && !isActive) ||
            (activeFilterId === false && isActive)
          ) {
            return false;
          }
        }

        return true;
      }),
    [exerciseLoadFormulas, filter, filterFormik.values],
  );

  const cellReducers = getCellReducers(
    t,
    classes,
    {
      canEdit: access?.exerciseLoadFormulas?.canEdit ?? false,
      canDelete: access?.exerciseLoadFormulas?.canDelete ?? false,
    },
    (formula: IExerciseLoadFormula) => {
      setItemForEditing(formula);
      setShowEditModal(true);
    },
    (formula: IExerciseLoadFormula) => {
      setItemForDelete(formula);
    },
  );

  const useTableData = useFlexibleColumnsTable(
    filteredValues,
    LOCAL_STORAGE_KEYS.EXERCISE_LOAD_FORMULA_TABLE_SETTINGS,
    getTableColumns(t),
    orderBy,
    defaultOrder,
  );

  const {
    order,
    columnsListForPopup,
    columnsList,
    onToggleHideColumn,
    onRestoreDefault,
    sortedArray,
    onSortColumns,
    onSort,
  } = useTableData;

  function onItemDelete() {
    setItemForDelete(null);
    deleteExerciseLoadFormula(itemForDelete.id);
  }

  const filterActiveOptions = [
    allOption,
    { id: true, name: t('common.active') },
    { id: false, name: t('common.notActive') },
  ];

  return {
    sortedArray,
    columnsList,
    order,
    orderBy,
    onSort,
    onSortColumns,
    cellReducers,
    itemForDelete,
    setItemForDelete,
    onItemDelete,
    filter,
    setFilter,
    showEditModal,
    setShowEditModal,
    itemForEditing,
    setItemForEditing,
    filterFormik,
    filterActiveOptions,
  };
}
